import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import {
  Building2, ShieldAlert, FileText, CreditCard, Users, Activity,
  Server, Database, Plus, AlertTriangle, Package,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

function formatTimeRemaining(expiresAt) {
  const now = new Date();
  const diff = new Date(expiresAt) - now;
  if (diff <= 0) return 'utgången';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h >= 1) return `${h}h ${m}m kvar`;
  return `${m}m kvar`;
}

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [custResp, contractResp, planResp, sessResp, healthResp] = await Promise.all([
          api.get('/admin/customers'),
          api.get('/admin/contracts').catch(() => ({ data: [] })),
          api.get('/admin/plans').catch(() => ({ data: [] })),
          api.get('/admin/support-sessions', { params: { active: true } }),
          api.get('/health').catch(() => ({ data: null })),
        ]);
        if (cancelled) return;

        setCustomers(Array.isArray(custResp.data) ? custResp.data : []);
        setContracts(Array.isArray(contractResp.data) ? contractResp.data : []);
        setPlans(Array.isArray(planResp.data) ? planResp.data : []);
        setActiveSessions(Array.isArray(sessResp.data) ? sessResp.data : []);
        setHealth(healthResp.data);
      } catch (err) {
        toast.error('Kunde inte hämta plattformsdata');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const totalActiveCustomers = customers.filter((c) => c.status === 'aktiv').length;
  const activeContracts = contracts.filter((c) => c.status === 'aktivt').length;
  const totalActiveUsers = customers.reduce(
    (acc, c) => acc + (c.active_users_count || 0), 0,
  );
  const totalParticipants = customers.reduce(
    (acc, c) => acc + (c.active_participants_count || 0), 0,
  );

  const planMonthlyMap = Object.fromEntries(
    plans.map((p) => [p.code, p.monthly_price_sek || 0]),
  );
  const monthlyRevenue = contracts.reduce((acc, c) => {
    if (c.status !== 'aktivt') return acc;
    // Använd kontraktets sparade pris om det finns, annars plan-pris
    const price = c.monthly_price || planMonthlyMap[c.package_code] || 0;
    return acc + Number(price);
  }, 0);

  const customersWithWarnings = customers.filter(
    (c) => Array.isArray(c.warnings) && c.warnings.length > 0,
  );

  const greeting = user?.name ? `Välkommen tillbaka, ${user.name}` : 'Välkommen tillbaka';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="super-admin-dashboard">
        <div>
          <h1 className="text-2xl font-bold">{greeting}</h1>
          <p className="text-sm text-gray-600 mt-1">
            Plattformsöversikt — aggregerad metadata. För att se kunddata,
            aktivera en supportsession.
          </p>
        </div>

        {/* Plattformsmetrik */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase text-gray-500 flex items-center gap-1">
                <Building2 size={14} /> Aktiva kunder
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{totalActiveCustomers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase text-gray-500 flex items-center gap-1">
                <FileText size={14} /> Aktiva avtal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{activeContracts}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase text-gray-500 flex items-center gap-1">
                <Users size={14} /> Aktiva användare
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{totalActiveUsers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase text-gray-500 flex items-center gap-1">
                <CreditCard size={14} /> MRR (uppskattat)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{monthlyRevenue.toLocaleString('sv-SE')} kr</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktiva supportsessioner */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert size={18} />
              Aktiva supportsessioner ({activeSessions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeSessions.length === 0 ? (
              <p className="text-sm text-gray-500">
                Inga aktiva supportsessioner just nu.
              </p>
            ) : (
              <ul className="space-y-2">
                {activeSessions.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between text-sm border-b pb-2 last:border-b-0"
                  >
                    <span className="font-medium">{s.target_org_name || s.target_org_id}</span>
                    <span className="text-gray-600">
                      {formatTimeRemaining(s.expires_at)} · {s.access_count} anrop
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Onboarding-status — kunder med varningar */}
        {customersWithWarnings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <AlertTriangle size={18} className="text-orange-600" />
                {customersWithWarnings.length} kund(er) behöver åtgärdas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {customersWithWarnings.slice(0, 5).map((c) => (
                  <li key={c.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-b-0">
                    <Link to={`/admin/customers/${c.id}`} className="font-medium hover:underline">
                      {c.name}
                    </Link>
                    <span className="text-orange-700">
                      {c.warnings.join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/admin/customers"
                className="inline-block mt-3 text-sm font-semibold underline"
              >
                Visa alla kunder →
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Kundnyckeltal (utan namn på persondata) */}
        <Card>
          <CardHeader>
            <CardTitle>Kundnyckeltal (aggregerat)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500">Laddar...</p>
            ) : customers.length === 0 ? (
              <p className="text-sm text-gray-500">Inga kunder registrerade.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Kund</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold">Användare</th>
                      <th className="px-3 py-2 font-semibold">Deltagare</th>
                      <th className="px-3 py-2 font-semibold">Avtal</th>
                      <th className="px-3 py-2 font-semibold">Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} className="border-t">
                        <td className="px-3 py-2 font-medium">
                          <Link to={`/admin/customers/${c.id}`} className="hover:underline">
                            {c.name}
                          </Link>
                        </td>
                        <td className="px-3 py-2 capitalize">{c.status}</td>
                        <td className="px-3 py-2">{c.active_users_count ?? 0}</td>
                        <td className="px-3 py-2">{c.active_participants_count ?? 0}</td>
                        <td className="px-3 py-2">{c.has_active_contract ? '✓' : '—'}</td>
                        <td className="px-3 py-2">{c.has_admin ? '✓' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Systemstatus */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity size={18} />
              Systemstatus
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!health ? (
              <p className="text-sm text-gray-500">Kunde inte läsa systemstatus.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Server size={14} className="text-gray-500" />
                  <span>API: </span>
                  <span className={`font-semibold ${health.status === 'healthy' ? 'text-green-700' : health.status === 'degraded' ? 'text-yellow-700' : 'text-red-700'}`}>
                    {health.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Database size={14} className="text-gray-500" />
                  <span>Databas: </span>
                  <span className={`font-semibold ${health.db === 'connected' ? 'text-green-700' : 'text-red-700'}`}>
                    {health.db}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Version:</span>
                  <span className="font-mono text-xs">{health.version}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Snabblänkar */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Link to="/admin/customers" className="block">
            <Card className="hover:shadow transition">
              <CardContent className="flex items-center gap-3 py-4">
                <Building2 size={20} className="text-gray-600" />
                <span className="font-semibold">Kundorganisationer</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/subscription-plans" className="block">
            <Card className="hover:shadow transition">
              <CardContent className="flex items-center gap-3 py-4">
                <Package size={20} className="text-gray-600" />
                <span className="font-semibold">Prenumerationsplaner</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/contracts" className="block">
            <Card className="hover:shadow transition">
              <CardContent className="flex items-center gap-3 py-4">
                <FileText size={20} className="text-gray-600" />
                <span className="font-semibold">Avtal</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/support-sessions" className="block">
            <Card className="hover:shadow transition">
              <CardContent className="flex items-center gap-3 py-4">
                <Plus size={20} className="text-gray-600" />
                <span className="font-semibold">Supportsessioner</span>
              </CardContent>
            </Card>
          </Link>
          <Link to="/users-admin" className="block">
            <Card className="hover:shadow transition">
              <CardContent className="flex items-center gap-3 py-4">
                <Users size={20} className="text-gray-600" />
                <span className="font-semibold">Användare</span>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
